import {action, makeObservable, observable} from "mobx";
import {inject} from "react-ioc";
import {ApiClients} from "app/modules/company/services/AxiosBaseClient";
import {FormState, FieldState} from "formstate";
import {requiredNumber} from "app/modules/common/form/validators";

export class AdminDevControllerStore {
    @inject apiClients: ApiClients;

    isLoading: boolean = false;
    isInitLoading: boolean = false;
    lastResult: string = "";
    emailIntegrations: any[] = [];

    formClearEmailBoxState = new FormState({
        integrationId: new FieldState<number | undefined>(undefined).validators(requiredNumber)
    });

    constructor() {
        makeObservable(this, {
            isLoading: observable,
            isInitLoading: observable,
            lastResult: observable,
            emailIntegrations: observable,
            init: action,
            setLoading: action,
            setLastResult: action,
            importZipCodes: action,
            returnTimeZones: action,
            getVehicleOrders: action,
            getAllOrders: action,
            clearEmailBox: action,
            refreshAllVehicleOrders: action,
            setRecurringMasterJobs: action,
            refreshVehicleAvailability: action,
            populateOrderMetrics: action,
            sylectusReadVehicles: action,
            getVehicleAvailabilityRedis: action
        });
    }

    setLoading = (loading: boolean) => {
        this.isLoading = loading;
    }

    setLastResult = (result: string) => {
        this.lastResult = result;
    }

    init = async () => {
        this.isInitLoading = true;
        try {
            // Load email integrations
            this.emailIntegrations = await this.apiClients.emailInboxClient.getEmailIntegrations();
        } catch (error) {
            console.error("Error initializing admin dev controller:", error);
        } finally {
            this.isInitLoading = false;
        }
    }

    importZipCodes = async () => {
        this.setLoading(true);
        try {
            await this.apiClients.devClient.importZipCodes();
            this.setLastResult("Zip codes imported successfully");
        } catch (error) {
            console.error("Error importing zip codes:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to import zip codes'}`);
        } finally {
            this.setLoading(false);
        }
    }

    returnTimeZones = async () => {
        this.setLoading(true);
        try {
            const timeZones = await this.apiClients.devClient.returnTimeZones();
            this.setLastResult(`Retrieved ${timeZones.length} time zones`);
        } catch (error) {
            console.error("Error getting time zones:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to get time zones'}`);
        } finally {
            this.setLoading(false);
        }
    }

    getVehicleOrders = async (id?: number) => {
        this.setLoading(true);
        try {
            await this.apiClients.devClient.getVehicleOrders(id);
            this.setLastResult("Vehicle orders retrieved successfully");
        } catch (error) {
            console.error("Error getting vehicle orders:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to get vehicle orders'}`);
        } finally {
            this.setLoading(false);
        }
    }

    getAllOrders = async () => {
        this.setLoading(true);
        try {
            await this.apiClients.devClient.getAllOrders();
            this.setLastResult("All orders retrieved successfully");
        } catch (error) {
            console.error("Error getting all orders:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to get all orders'}`);
        } finally {
            this.setLoading(false);
        }
    }

    clearEmailBox = async () => {
        this.setLoading(true);
        try {
            const integrationId = this.formClearEmailBoxState.$.integrationId.value;
            await this.apiClients.devClient.clearEmailBox(integrationId);
            this.setLastResult("Email box cleared successfully");
        } catch (error) {
            console.error("Error clearing email box:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to clear email box'}`);
        } finally {
            this.setLoading(false);
        }
    }

    refreshAllVehicleOrders = async () => {
        this.setLoading(true);
        try {
            await this.apiClients.devClient.refreshAllVehicleOrders();
            this.setLastResult("All vehicle orders refreshed successfully");
        } catch (error) {
            console.error("Error refreshing all vehicle orders:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to refresh all vehicle orders'}`);
        } finally {
            this.setLoading(false);
        }
    }

    setRecurringMasterJobs = async () => {
        this.setLoading(true);
        try {
            await this.apiClients.devClient.setRecurringMasterJobs();
            this.setLastResult("Recurring master jobs set successfully");
        } catch (error) {
            console.error("Error setting recurring master jobs:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to set recurring master jobs'}`);
        } finally {
            this.setLoading(false);
        }
    }

    refreshVehicleAvailability = async () => {
        this.setLoading(true);
        try {
            await this.apiClients.devClient.refreshVehicleAvailability();
            this.setLastResult("Vehicle availability refreshed successfully");
        } catch (error) {
            console.error("Error refreshing vehicle availability:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to refresh vehicle availability'}`);
        } finally {
            this.setLoading(false);
        }
    }

    populateOrderMetrics = async () => {
        this.setLoading(true);
        try {
            await this.apiClients.devClient.populateOrderMetrics();
            this.setLastResult("Order metrics populated successfully");
        } catch (error) {
            console.error("Error populating order metrics:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to populate order metrics'}`);
        } finally {
            this.setLoading(false);
        }
    }

    sylectusReadVehicles = async () => {
        this.setLoading(true);
        try {
            await this.apiClients.devClient.sylectusReadVehicles();
            this.setLastResult("Sylectus vehicles read successfully");
        } catch (error) {
            console.error("Error reading Sylectus vehicles:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to read Sylectus vehicles'}`);
        } finally {
            this.setLoading(false);
        }
    }

    getVehicleAvailabilityRedis = async () => {
        this.setLoading(true);
        try {
            const availability = await this.apiClients.devClient.getVehicleAvailabilityRedis();
            this.setLastResult(`Retrieved ${availability.length} vehicle availability records from Redis`);
        } catch (error) {
            console.error("Error getting vehicle availability from Redis:", error);
            this.setLastResult(`Error: ${error.message || 'Failed to get vehicle availability from Redis'}`);
        } finally {
            this.setLoading(false);
        }
    }
}
